import { useEffect } from 'react'

import { STORAGE_KEYS } from '@/shared/utils/constants'

import type { ThemeMode } from './ThemeProvider'
import { useDarkMode } from './useDarkMode'

/**
 * Applies the user's SERVER-persisted theme preference (from the authenticated
 * `/me` hydration, e.g. `user.preferences.theme`) on login — but ONLY when the
 * device has no explicit local theme choice yet.
 *
 * Precedence (highest wins):
 *   1. Explicit local choice — `STORAGE_KEYS.THEME_PREFERENCE` is present. The
 *      navbar toggle and the Preferences dropdown both write it, so a
 *      device-local override survives reloads (Repro B).
 *   2. Server preference — applied here on a fresh device / first login so the
 *      saved theme is honored on load (Repro A).
 *   3. System / default — handled by the ThemeProvider itself.
 *
 * It applies through the ThemeProvider (`setMode`), so the provider's React
 * state (and therefore the navbar toggle's icon) stays in sync with the DOM —
 * no in-session divergence. `setMode` persists both theme keys, which also
 * closes the precedence gate above, so this seeds at most once per device.
 */
export function useApplyServerThemePreference(serverTheme: ThemeMode | undefined): void {
  const { setMode } = useDarkMode()

  useEffect(() => {
    if (serverTheme === undefined) return
    if (typeof window === 'undefined') return

    // A present THEME_PREFERENCE means the user has explicitly chosen a theme on
    // THIS device (navbar toggle / Preferences dropdown). Never override it — the
    // device-local choice must win over the server default.
    if (localStorage.getItem(STORAGE_KEYS.THEME_PREFERENCE) !== null) return

    setMode(serverTheme)
  }, [serverTheme, setMode])
}
